import axios from "axios";
import { useLocalStorage } from "../Helpers/CustomHooks/useLocalStorage";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to include Authorization header for all requests
api.interceptors.request.use(
  (config) => {
    const user = localStorage.getItem("user");

    if (user && user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const login = async (email, password) => {
  try {
    const response = await api.post("/login", { email, password });
    const { data } = response.data;

    return data;
  } catch (error) {
    throw error;
  }
};

export const logout = async (email) => {
  try {
    await api.post("/logout", { email });
  } catch (error) {
    throw error;
  }
};

export const getPhysician = async (physicianId) => {
  try {
    const response = await api.get(`/${physicianId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const changePassword = async (physicianId, body) => {
  try {
    const response = await api.patch(`/${physicianId}/change-password`, body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const editProfile = async (physicianId, body) => {
  try {
    const response = await api.patch(`/${physicianId}/edit-profile`, body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPatients = async (physicianId, analyticsType) => {
  try {
    let response = {};
    if (analyticsType) {
      response = await api.get(
        `/${physicianId}/patients?type=${analyticsType}`
      );
    } else {
      response = await api.get(`/${physicianId}/patients`);
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPatientById = async (physicianId, patientId) => {
  try {
    const response = await api.get(`/${physicianId}/patients/${patientId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const editPatientDetails = async (physicianId, patientId, payload) => {
  try {
    const response = await api.patch(
      `/${physicianId}/patients/${patientId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPatientTimeline = async (physicianId, patientId) => {
  try {
    const response = await api.get(
      `/${physicianId}/patients/${patientId}/timeline`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCuratedFilesList = async (physicianId, patientId, fileid) => {
  try {
    let response = {};
    if (fileid) {
      response = await api.get(
        `/${physicianId}/patients/${patientId}/investigations-and-treatments?fileid=${fileid}`
      );
    } else {
      response = await api.get(
        `/${physicianId}/patients/${patientId}/investigations-and-treatments`
      );
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getReportTypes = async (physicianId, patientId, type) => {
  try {
    let response = await api.get(
      `/${physicianId}/patients/${patientId}/investigations-and-treatments/report-types?type=${type}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadCuratedFile = async (physicianId, patientId, body) => {
  try {
    const response = await api.post(
      `/${physicianId}/patients/${patientId}/investigations-and-treatments/download`,
      body
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const editCuratedFile = async (physicianId, patientId, fileid, body) => {
  try {
    const response = await api.patch(
      `/${physicianId}/patients/${patientId}/investigations-and-treatments/edit/${fileid}`,
      body
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addRemark = async (physicianId, patientId, body) => {
  try {
    const response = await api.patch(
      `/${physicianId}/patients/${patientId}/timeline`,
      body
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateCaseSummary = async (physicianId, patientId, body) => {
  try {
    const response = await api.patch(
      `/${physicianId}/patients/${patientId}/case-summary`,
      body
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getClinicalParams = async (physicianId) => {
  try {
    const response = await api.get(`/${physicianId}/clinical-params`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPatientMessageConversations = async (physicianId) => {
  try {
    const response = await api.get(`/${physicianId}/patients/message/all`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPatientMessages = async (physicianId, patientId) => {
  try {
    const response = await api.get(
      `/${physicianId}/patients/${patientId}/message`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendMessageToPatient = async (physicianId, patientId, body) => {
  try {
    const response = await api.post(
      `/${physicianId}/patients/${patientId}/message`,
      body
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;
